// Sidebar.jsx (Admin) — JS only, design matches your provided file
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../assets/RCS-Logo-White.png";
import CollapsedLogo from "../assets/RCS-Logo-White.png";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/auth-slice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import clsx from "clsx";

const Sidebar = ({ forceExpanded = false }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const { first_name = "", last_name = "", department = "" } = user || {};
  const isLiquidationApprover = Number(user?.user_id || user?.id || 0) === 127;
  const profileLink = location.pathname.startsWith("/accounting")
    ? "/accounting/profile"
    : "/admin/profile";
  const isJoLedgerActive = location.pathname.startsWith("/admin/jo-ledger");
  const isJoRequestsActive = location.pathname.startsWith("/admin/jo-requests");

  // Use same logic as your super-admin sidebar, but only to toggle text visibility.
  const [isLargeScreen, setIsLargeScreen] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [textVisible, setTextVisible] = useState(() => {
    if (typeof window === "undefined") return true;
    return Boolean(forceExpanded || window.innerWidth >= 1024);
  });
  const [openShipments, setOpenShipments] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const width = window.innerWidth;
      const isLarge = Boolean(forceExpanded || width >= 1025);
      setIsLargeScreen(isLarge);
      setTextVisible(isLarge);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // initialize
    return () => window.removeEventListener("resize", handleResize);
  }, [forceExpanded]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    dispatch(logoutUser()).then((data) => {
      const message = data?.payload?.message;
      if (data?.payload?.success) {
        toast.success(message, { position: "top-right" });
      } else {
        toast.error(message, { position: "top-right" });
      }
      sessionStorage.removeItem("passwordResetNotificationClosed");
    });
  };

  const handleLogoutConfirmation = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-3xl shadow-2xl max-w-md mx-4 w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="fas fa-sign-out-alt text-white text-xl"></i>
              </div>
              <h2 className="text-2xl font-bold text-blue mb-2">
                Confirm Logout
              </h2>
              <p className="text-grey">
                Are you sure you want to sign out of your account?
              </p>
            </div>
            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-grey font-semibold rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  onClose();
                }}
                className="flex-1 px-6 py-3 bg-red text-white font-semibold rounded-xl hover:bg-red/90 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      ),
    });
  };

  return (
    // CONTENT-ONLY: no fixed/positioning/width here; AdminLayout controls that.
    <div className="flex font-poppins h-full">
      {/* Modern Sidebar (visual wrapper) */}
      <div
        className={clsx(
          // removed fixed/left/width to avoid fighting the layout; keep all visual classes
          "h-full bg-blue text-white transition-all duration-500 ease-out backdrop-blur-xl shadow-2xl border-r border-white/10 relative",
          // Layout provides the actual width; we still respond to label visibility with heights etc.
          // (No w-72/w-20 here to prevent double widths)
          "w-full"
        )}
        style={{ willChange: "transform", transform: "translateZ(0)" }}
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-4 w-32 h-32 bg-yellow/5 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-32 left-4 w-24 h-24 bg-white/5 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        {/* Logo Section */}
        <div
          className={`relative flex items-center justify-center p-6 border-b border-white/10 backdrop-blur-sm ${
            isLargeScreen ? "h-28" : "h-20"
          }`}
        >
          <div className="relative">
            <img
              src={isLargeScreen ? Logo : CollapsedLogo}
              alt="Logo"
              className={`transition-all duration-500 ${
                isLargeScreen
                  ? "w-44 h-16 object-contain"
                  : "w-12 h-12 object-contain"
              }`}
            />
          </div>
        </div>

        {/* Navigation Menu — routes switched to /admin/* */}
        <nav
          className="mt-8 px-4 space-y-3 overflow-y-auto pb-32"
          style={{ maxHeight: "calc(100vh - 16rem)" }}
        >
          <Link
            to="/admin/dashboard"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/admin/dashboard")
                ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
          >
            <div
              className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                isActive("/admin/dashboard")
                  ? "text-blue"
                  : "text-white hover:text-yellow"
              }`}
            >
              <i className="fa-solid fa-chart-line text-lg"></i>
              {isActive("/admin/dashboard") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {textVisible && (
              <span
                className={`font-semibold transition-all duration-300 ${
                  isActive("/admin/dashboard")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}
              >
                Dashboard
              </span>
            )}

            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          <div className="mt-2">
            <button
              type="button"
              onClick={() => setOpenShipments((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wide text-white/70 hover:text-white transition-colors"
            >
              <i
                className={`fa-solid fa-chevron-${
                  openShipments ? "down" : "right"
                } text-[10px]`}
              ></i>
              {textVisible && <span>Shipments</span>}
            </button>

            {openShipments && (
              <div className="space-y-2 mt-2">
                <Link
                  to="/admin/create-shipment"
                  className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                    isLargeScreen ? "justify-start" : "justify-center"
                  } ${
                    isActive("/admin/create-shipment")
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  }`}
                >
                  <div
                    className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                      isActive("/admin/create-shipment")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    <i className="fas fa-shipping-fast text-lg"></i>
                    {isActive("/admin/create-shipment") && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <span
                      className={`font-semibold transition-all duration-300 ${
                        isActive("/admin/create-shipment")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      Create Shipment
                    </span>
                  )}
                </Link>

                <Link
                  to="/admin/shipment-management"
                  className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
                    isLargeScreen ? "justify-start" : "justify-center"
                  } ${
                    isActive("/admin/shipment-management")
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  }`}
                >
                  <div
                    className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                      isActive("/admin/shipment-management")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    <i className="fa-solid fa-dolly text-lg"></i>
                    {isActive("/admin/shipment-management") && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <span
                      className={`font-semibold transition-all duration-300 ${
                        isActive("/admin/shipment-management")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      Shipment Managements
                    </span>
                  )}
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/admin/billed-shipments"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/admin/billed-shipments")
                ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
            style={{ animationDelay: "350ms" }}
          >
            <div
              className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                isActive("/admin/billed-shipments")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              }`}
            >
              <i className="fa-solid fa-file-invoice-dollar text-lg"></i>
              {isActive("/admin/billed-shipments") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {textVisible && (
              <span
                className={`font-semibold transition-all duration-300 ${
                  isActive("/admin/billed-shipments")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}
              >
                Billed Shipments
              </span>
            )}

            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          {/* Billing Upload - Only visible for Accounting department */}
          {department === "Accounting" && (
            <Link
              to="/admin/billing-upload"
              className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                isLargeScreen ? "justify-start" : "justify-center"
              } ${
                isActive("/admin/billing-upload")
                  ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                  : "text-white hover:bg-white/10 hover:shadow-lg"
              }`}
              style={{ animationDelay: "400ms" }}
            >
              <div
                className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                  isActive("/admin/billing-upload")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}
              >
                <i className="fa-solid fa-file-invoice-dollar text-lg"></i>
                {isActive("/admin/billing-upload") && (
                  <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                )}
              </div>

              {textVisible && (
                <span
                  className={`font-semibold transition-all duration-300 ${
                    isActive("/admin/billing-upload")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  Billing Upload
                </span>
              )}

              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>
          )}

          {/* Billing List - Visible for all */}
          <Link
            to="/admin/billing-list"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/admin/billing-list")
                ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
            style={{ animationDelay: "500ms" }}
          >
            <div
              className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                isActive("/admin/billing-list")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              }`}
            >
              <i className="fa-solid fa-file-invoice text-lg"></i>
              {isActive("/admin/billing-list") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {textVisible && (
              <span
                className={`font-semibold transition-all duration-300 ${
                  isActive("/admin/billing-list")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}
              >
                Billing List
              </span>
            )}

            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          <Link
            to="/admin/document-templates"
            className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
              isLargeScreen ? "justify-start" : "justify-center"
            } ${
              isActive("/admin/document-templates")
                ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                : "text-white hover:bg-white/10 hover:shadow-lg"
            }`}
            style={{ animationDelay: "600ms" }}
          >
            <div
              className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                isActive("/admin/document-templates")
                  ? "text-blue"
                  : "text-white group-hover:text-yellow"
              }`}
            >
              <i className="fa-solid fa-file-lines text-lg"></i>
              {isActive("/admin/document-templates") && (
                <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
              )}
            </div>

            {textVisible && (
              <span
                className={`font-semibold transition-all duration-300 ${
                  isActive("/admin/document-templates")
                    ? "text-blue"
                    : "text-white group-hover:text-yellow"
                }`}
              >
                Document Templates
              </span>
            )}

            <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>

          <div className="mt-4">
            {textVisible && (
              <p className="px-4 text-xs uppercase tracking-[0.12em] text-white/60 mb-2">
                Accounting Process
              </p>
            )}
            <div className="space-y-2">
              <Link
                to="/admin/request-create"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/admin/request-create")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "560ms" }}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/admin/request-create")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-file-circle-plus text-lg"></i>
                  {isActive("/admin/request-create") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isActive("/admin/request-create")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    Request Creation
                  </span>
                )}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/admin/request-summary"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/admin/request-summary")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "600ms" }}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/admin/request-summary")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-table-list text-lg"></i>
                  {isActive("/admin/request-summary") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isActive("/admin/request-summary")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    Request Summary
                  </span>
                )}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/admin/cost-analysis"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/admin/cost-analysis")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "620ms" }}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/admin/cost-analysis")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-calculator text-lg"></i>
                  {isActive("/admin/cost-analysis") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isActive("/admin/cost-analysis")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    Cost Analysis Workflow
                  </span>
                )}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/admin/liquidation"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/admin/liquidation")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "640ms" }}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/admin/liquidation")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-receipt text-lg"></i>
                  {isActive("/admin/liquidation") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isActive("/admin/liquidation")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    Liquidation Workflow
                  </span>
                )}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>

              <Link
                to="/admin/liquidation-approvals"
                className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isActive("/admin/liquidation-approvals")
                    ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
                style={{ animationDelay: "680ms" }}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isActive("/admin/liquidation-approvals")
                      ? "text-blue"
                      : "text-white group-hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-user-check text-lg"></i>
                  {isActive("/admin/liquidation-approvals") && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>
                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isActive("/admin/liquidation-approvals")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    Leader Liquidation Approval
                  </span>
                )}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              {isLiquidationApprover ? (
                <Link
                  to="/admin/liquidation-user127-approvals"
                  className={`group relative flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                    isLargeScreen ? "justify-start" : "justify-center"
                  } ${
                    isActive("/admin/liquidation-user127-approvals")
                      ? "bg-yellow text-blue shadow-lg shadow-yellow/25"
                      : "text-white hover:bg-white/10 hover:shadow-lg"
                  }`}
                  style={{ animationDelay: "720ms" }}
                >
                  <div
                    className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                      isActive("/admin/liquidation-user127-approvals")
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    <i className="fa-solid fa-circle-check text-lg"></i>
                    {isActive("/admin/liquidation-user127-approvals") && (
                      <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                    )}
                  </div>
                  {textVisible && (
                    <span
                      className={`font-semibold transition-all duration-300 ${
                        isActive("/admin/liquidation-user127-approvals")
                          ? "text-blue"
                          : "text-white group-hover:text-yellow"
                      }`}
                    >
                      Liquidation Approvals
                    </span>
                  )}
                  <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              ) : null}
              <Link
                to="/admin/jo-ledger"
                className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isJoLedgerActive
                    ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isJoLedgerActive
                      ? "text-blue"
                      : "text-white hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-layer-group text-lg"></i>
                  {isJoLedgerActive && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>

                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isJoLedgerActive
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    JO Ledger
                  </span>
                )}

                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
              <Link
                to="/admin/jo-requests"
                className={`group relative flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${
                  isLargeScreen ? "justify-start" : "justify-center"
                } ${
                  isJoRequestsActive
                    ? "bg-gradient-to-r from-yellow to-yellow/90 text-blue shadow-lg shadow-yellow/25"
                    : "text-white hover:bg-white/10 hover:shadow-lg"
                }`}
              >
                <div
                  className={`relative flex items-center justify-center w-6 h-6 transition-all duration-300 ${
                    isJoRequestsActive
                      ? "text-blue"
                      : "text-white hover:text-yellow"
                  }`}
                >
                  <i className="fa-solid fa-diagram-project text-lg"></i>
                  {isJoRequestsActive && (
                    <div className="absolute inset-0 bg-blue/20 rounded-full blur-sm animate-pulse"></div>
                  )}
                </div>

                {textVisible && (
                  <span
                    className={`font-semibold transition-all duration-300 ${
                      isJoRequestsActive
                        ? "text-blue"
                        : "text-white group-hover:text-yellow"
                    }`}
                  >
                    JO Requests
                  </span>
                )}

                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>
          </div>
        </nav>

        {/* Profile Section */}
        <div className="absolute bottom-0 left-0 w-full border-t border-white/10 backdrop-blur-sm">
          <div className="p-6 space-y-3">
            <Link
              to={profileLink}
              className={`flex items-center gap-4 ${
                isLargeScreen ? "justify-start" : "justify-center"
              } hover:bg-white/10 rounded-2xl px-2 py-1 transition-colors`}
            >
              <div className="relative">
                <div className="bg-yellow text-blue font-bold rounded-2xl w-12 h-12 flex items-center justify-center shadow-lg">
                  {`${first_name?.[0] || ""}${last_name?.[0] || ""}`}
                </div>
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-sm">
                  <div className="w-full h-full bg-green-500 rounded-full animate-ping opacity-75"></div>
                </div>
              </div>
              {textVisible && (
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm truncate">{`${first_name} ${last_name}`}</p>
                  <p className="text-xs text-white/70 truncate">View Profile</p>
                </div>
              )}
            </Link>

            <button
              className={clsx(
                "group relative flex items-center gap-3 w-full px-4 py-3",
                "bg-red hover:bg-red/90",
                "text-white rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl",
                isLargeScreen ? "justify-start" : "justify-center"
              )}
              onClick={handleLogoutConfirmation}
            >
              {/* Content above the overlay */}
              <div className="relative z-10 flex items-center gap-3">
                <div className="flex items-center justify-center w-5 h-5">
                  <i className="fa-solid fa-right-from-bracket text-sm group-hover:text-yellow transition-colors duration-300"></i>
                </div>

                {textVisible && (
                  <span className="font-semibold text-sm group-hover:text-yellow transition-colors duration-300">
                    Logout
                  </span>
                )}
              </div>

              {/* Glow overlay lives under the text and ignores pointer events */}
              <div className="pointer-events-none absolute inset-0 z-0 rounded-2xl bg-red/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
